export const ADDITIONAL_ATTENDEES_TYPE = 'additionalAttendees';
// Well-known roles for the mandatory sub-questions - admins can't remove or change the type of a
// role-tagged sub-question, so downstream code (guest name resolution for Transport, and later any
// per-guest email feature) can rely on these existing and being the stated type.
export const ADDITIONAL_ATTENDEE_SUB_FIELD_ROLES = {
    FirstName: 'firstName',
    LastName: 'lastName',
    Email: 'email'
};
// Sub-questions may only use the "standard" input palette (FieldTypes 1-12) - advanced/bound
// types (Company Dropdown, Pickup Location, Undertaking) and preset types don't make sense nested
// inside a repeating group, and Additional Attendees can't nest inside itself.
export const ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES = [
    'text',
    'email',
    'phone',
    'date',
    'time',
    'multiline',
    'radio',
    'choice',
    'checkbox-group',
    'dropdown',
    'multi-select',
    'toggle'
];
export const ADDITIONAL_ATTENDEE_OPTION_FIELD_TYPES = [
    'dropdown',
    'radio',
    'choice',
    'checkbox-group',
    'multi-select',
    'toggle'
];
// Maps a sub-question's builder-side string type (e.g. 'text', 'dropdown') to the numeric
// FieldType string that `fieldRenderers.ts` (the attendee-facing renderer table) is keyed by -
// the same mapping `getFieldTypeNumber` performs for top-level fields in CreateEvent/index.tsx.
const SUB_FIELD_TYPE_TO_RENDERER_KEY = {
    text: '1',
    email: '2',
    phone: '3',
    date: '4',
    time: '5',
    multiline: '6',
    radio: '7',
    choice: '8',
    'checkbox-group': '9',
    dropdown: '10',
    'multi-select': '11',
    toggle: '12'
};
export const getAdditionalAttendeeSubFieldRendererKey = (fieldType) => SUB_FIELD_TYPE_TO_RENDERER_KEY[fieldType] ?? '1';
export const isOptionBasedAttendeeSubField = (fieldType) => ADDITIONAL_ATTENDEE_OPTION_FIELD_TYPES.includes(fieldType);
export const createDefaultAttendeeSubFieldOptions = (fieldType, t, existingOptions) => {
    if (!isOptionBasedAttendeeSubField(fieldType))
        return undefined;
    if (existingOptions && existingOptions.length > 0)
        return existingOptions;
    if (fieldType === 'toggle') {
        return [
            {
                id: crypto.randomUUID(),
                value: 1,
                label: t('events.toggleOptions.yes'),
                isFromBackend: false
            },
            {
                id: crypto.randomUUID(),
                value: 0,
                label: t('events.toggleOptions.no'),
                isFromBackend: false
            }
        ];
    }
    return [
        {
            id: crypto.randomUUID(),
            label: 'Option 1',
            value: 1,
            isFromBackend: false
        }
    ];
};
// Sub-questions become real sibling FormFields on save (RelatedFormFieldId = the parent's id) -
// their ids must be real GUIDs like any other FormField, not semantic string keys, since the
// backend flattening in CreateEvent/index.tsx sends them straight through as FormField.Id.
export const createDefaultAdditionalAttendeeSubFields = (t) => {
    const partnerOptionId = crypto.randomUUID();
    const otherOptionId = crypto.randomUUID();
    return [
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.firstName'),
            fieldType: 'text',
            fieldParent: 'Single Line Input',
            fieldChild: 'Text',
            fieldOrder: 1,
            required: true,
            role: ADDITIONAL_ATTENDEE_SUB_FIELD_ROLES.FirstName
        },
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.middleName'),
            fieldType: 'text',
            fieldParent: 'Single Line Input',
            fieldChild: 'Text',
            fieldOrder: 2,
            required: false
        },
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.lastName'),
            fieldType: 'text',
            fieldParent: 'Single Line Input',
            fieldChild: 'Text',
            fieldOrder: 3,
            required: true,
            role: ADDITIONAL_ATTENDEE_SUB_FIELD_ROLES.LastName
        },
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.email'),
            fieldType: 'email',
            fieldParent: 'Single Line Input',
            fieldChild: 'Email',
            fieldOrder: 4,
            required: true,
            role: ADDITIONAL_ATTENDEE_SUB_FIELD_ROLES.Email
        },
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.phone'),
            fieldType: 'phone',
            fieldParent: 'Single Line Input',
            fieldChild: 'Phone',
            fieldOrder: 5,
            required: true
        },
        {
            id: crypto.randomUUID(),
            label: t('events.additionalAttendeesPreset.relationship'),
            fieldType: 'dropdown',
            fieldParent: 'Dropdown',
            fieldOrder: 6,
            required: true,
            options: [
                {
                    id: partnerOptionId,
                    label: t('events.additionalAttendeesPreset.relationshipOptions.partner'),
                    value: 1,
                    isFromBackend: false
                },
                {
                    id: otherOptionId,
                    label: t('events.additionalAttendeesPreset.relationshipOptions.other'),
                    value: 2,
                    isFromBackend: false
                }
            ]
        }
    ];
};
export const createAdditionalAttendeesField = ({ fieldOrder, t, existingCount = 0 }) => {
    const additionalAttendeeFields = createDefaultAdditionalAttendeeSubFields(t);
    return {
        id: crypto.randomUUID(),
        fieldType: ADDITIONAL_ATTENDEES_TYPE,
        fieldParent: 'Additional Attendees',
        // First one stays unnumbered (the common case - just one AA field) - only the 2nd, 3rd, etc.
        // get a number appended, since that's the only point multiple copies actually need telling apart.
        label: existingCount === 0
            ? t('events.additionalAttendeesPreset.title')
            : `${t('events.additionalAttendeesPreset.title')} ${existingCount + 1}`,
        descriptionForToolTip: t('events.additionalAttendeesPreset.description'),
        required: false,
        fieldOrder,
        isFromBackend: false,
        maxAdditionalAttendees: 2,
        fieldAdditionalNote: t('events.additionalAttendeesPreset.limitReachedMessageDefault'),
        additionalAttendeeFields
    };
};
